import React from 'react';
import { FaSearch, FaHandshake, FaShip, FaFileContract, FaChartLine } from 'react-icons/fa';
import SectionHeading from '../ui/SectionHeading';
import ScrollReveal from '../ui/ScrollReveal';

const TradingSection = () => {
  const steps = [
    {
      icon: FaSearch,
      title: 'Market Analysis',
      description: 'Our team of analysts continuously monitor global energy markets to identify optimal trading opportunities and anticipate market shifts.',
      delay: 0
    },
    {
      icon: FaHandshake,
      title: 'Strategic Partnerships',
      description: 'We connect with producers, refiners, and end-users to establish reliable supply chains and mutually beneficial trading relationships.',
      delay: 0.1
    },
    {
      icon: FaFileContract,
      title: 'Contract Negotiation',
      description: 'Our experienced traders structure customized agreements that address specific requirements while managing risk and ensuring compliance.',
      delay: 0.2
    },
    {
      icon: FaShip,
      title: 'Logistics Management',
      description: 'We coordinate efficient product transportation through our established network of shipping partners and terminal operators.',
      delay: 0.3
    },
    {
      icon: FaChartLine,
      title: 'Performance Monitoring',
      description: 'Throughout the trading cycle, we track execution quality, analyze results, and optimize strategies for continuous improvement.',
      delay: 0.4
    }
  ];

  return (
    <section id="trading" className="section-padding bg-black relative overflow-hidden">
      {/* Background gradient */}
      <div className="absolute bottom-0 left-0 translate-y-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full bg-secondary-500/10 blur-[100px] pointer-events-none"></div>
      
      <div className="container-custom relative z-10">
        <SectionHeading
          subtitle="Trading Process"
          title="Our Approach to Energy Trading"
          description="Caishen Capital employs a systematic, relationship-driven approach to hydrocarbon trading, focusing on market intelligence, strategic partnerships, and flawless execution."
        />
        
        <div className="mt-16 relative">
          {/* Background image */}
          <div 
            className="absolute inset-0 opacity-10 rounded-3xl overflow-hidden"
            style={{
              backgroundImage: 'url("https://images.unsplash.com/photo-1616421004055-e6c780c51241")',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              filter: 'grayscale(70%)'
            }}
          ></div>
          
          <div className="glass-card-strong p-10 md:p-12">
            <div className="relative">
              {/* Desktop view - Horizontal timeline */}
              <div className="hidden lg:block">
                <div className="absolute top-[9.5rem] left-0 right-0 h-1 bg-gradient-to-r from-primary-500 via-secondary-500 to-accent-gold"></div>
                
                <div className="grid grid-cols-5 gap-6">
                  {steps.map((step, index) => (
                    <ScrollReveal key={step.title} delay={step.delay} direction="up">
                      <div className="flex flex-col items-center text-center relative">
                        <div className="mb-10 flex flex-col items-center">
                          <div className="text-xl md:text-2xl font-bold mb-4">Step {index + 1}</div>
                          <div className="w-20 h-20 rounded-full bg-white/10 flex items-center justify-center shadow-lg mb-4 relative z-10">
                            <step.icon className="w-10 h-10 text-primary-400" />
                          </div>
                        </div>
                        
                        <h3 className="text-xl font-bold text-white mb-3">{step.title}</h3>
                        <p className="text-gray-300">{step.description}</p>
                      </div>
                    </ScrollReveal>
                  ))}
                </div>
              </div>
              
              {/* Mobile view - Vertical timeline */}
              <div className="lg:hidden space-y-12">
                {steps.map((step, index) => (
                  <ScrollReveal key={step.title} delay={step.delay * 0.5} direction="left">
                    <div className="flex">
                      <div className="mr-6 relative">
                        <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center shadow-lg relative z-10">
                          <step.icon className="w-6 h-6 text-primary-400" />
                        </div>
                        {index !== steps.length - 1 && (
                          <div className="absolute top-14 bottom-0 left-1/2 transform -translate-x-1/2 w-1 bg-gradient-to-b from-primary-500 to-secondary-500"></div>
                        )}
                      </div>
                      
                      <div className="flex-1">
                        <div className="text-lg font-bold mb-1 text-white">Step {index + 1}</div>
                        <h3 className="text-xl font-bold text-white mb-2">{step.title}</h3>
                        <p className="text-gray-300">{step.description}</p>
                      </div>
                    </div>
                  </ScrollReveal>
                ))}
              </div>
            </div>
            
          </div>
        </div>
      </div>
    </section>
  );
};

export default TradingSection;